import { z } from "zod";

import { SCHEMA_LIMITS } from "./constants";

const base64Url = z.string().regex(/^[A-Za-z0-9_-]+$/);

const EncryptedDataShape = z.object({
  // An empty field (an optional description) encrypts to an empty ciphertext.
  ciphertext: z.string().regex(/^[A-Za-z0-9_-]*$/),
  iv: base64Url,
  tag: base64Url,
});

function isEncryptedDataJson(value: string): boolean {
  try {
    return EncryptedDataShape.safeParse(JSON.parse(value)).success;
  } catch {
    return false;
  }
}

/**
 * One encrypted column value: the JSON that the browser's `encryptFields`
 * writes, `{ciphertext, iv, tag}`. Plain text sent in an `encrypted*` field
 * fails here.
 */
export function encryptedField(
  maxLength: number = SCHEMA_LIMITS.ENCRYPTED_BLOB_MAX_LENGTH,
) {
  return z
    .string()
    .max(maxLength, "Encrypted value is too long")
    .refine(isEncryptedDataJson, "Value is not encrypted");
}

/**
 * Cap for a long rich-text field (a vision, a category description). Its
 * ciphertext encodes the editor's JSON, which can be much longer than the
 * visible text.
 */
export const ENCRYPTED_RICH_TEXT_MAX_LENGTH = 262_144;

/** The row's `iv` column: the first field's base64url IV. */
export const encryptionIv = base64Url.max(64, "IV is too long");

/**
 * A plain-text key that an encrypted save must leave out: the browser sends
 * only the ciphertext. A save that still carries the text fails, and the
 * inferred type rejects it at compile time, so a caller that sends it is
 * caught instead of having the text stripped quietly on the server.
 */
export const plainTextRefused = z.undefined({
  message: "Send only the encrypted text",
});
